// (Note: TaskStatus type preserved as requested)
export type TaskStatus = 'backlog' | 'todo' | 'in_progress' | 'pantry' | 'finalized' | 'history' | 'done';

import React, { useMemo, useState } from 'react';
import { showToast } from '@/components/ui/Toast';
import { useTranslation } from 'react-i18next';
import {
  addDays, addWeeks, addMonths, subDays, subWeeks, subMonths, format,
  startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth,
} from 'date-fns';
import { writeBatch, doc, collection, setDoc, getDoc, getDocs, query, where, serverTimestamp } from 'firebase/firestore';
import { db } from '@/firebase';

// --- Types & Bricks ---
import { 
  AppState, 
  AppAction, 
  HouseholdMember,
  DataScope,
  Role,
  UserProfile 
} from '@/types';
import { 
  CalendarEvent as EngineEvent, 
  CustomCalendar,
} from '@/types';
import { CalendarToolbar } from '../features/calendar-engine/components/CalendarToolbar';
import { CalendarGrid } from '../features/calendar-engine/components/CalendarGrid';
import { EventModal } from '../features/calendar-engine/components/EventModal';
import { RecurrenceActionDialog, RecurrenceChoice } from '../features/calendar-engine/components/RecurrenceActionDialog';
import { CustomCalendarCreator } from '../features/calendar-engine/components/CustomCalendarCreator';

// --- Logic Engines ---
import { CalendarEngine } from '../features/calendar-engine/utils/calendarEngine';
import { addExdateToRecurrence, clampRecurrenceUntil, stripRecurrenceBounds } from '../features/calendar-engine/utils/recurrence';
import { expandRecurringEvents } from '../features/calendar-engine/utils/recurrence';
import { useTravelDurations, TravelPair } from '../features/calendar-engine/hooks/useTravelDurations';
import { useCalendarState } from '../features/calendar-engine/hooks/useCalendarState';
import { useAiAction } from '@/hooks/useAiAction';
import { Sparkles, Loader2 } from 'lucide-react';

interface CalendarProps {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

function isEngineEvent(event: EngineEvent | CustomCalendar): event is EngineEvent {
  return (event as EngineEvent).start !== undefined;
}

type ExtendedCalendarEventPayload = Partial<EngineEvent> & {
  googleEventId?: string;
  etag?: string;
  status?: string;
  htmlLink?: string;
  iCalUID?: string;
  recurrence?: string[];
  recurringEventId?: string;
  originalStartTime?: { dateTime: string; timeZone: string; };
  transparency?: string;
  visibility?: string;
  organizer?: { email: string; displayName: string; self: boolean; };
  attendees?: Array<{ email: string; responseStatus: string; comment?: string; }>;
  hangoutLink?: string;
  creatorId?: string;
  templateType?: string;
  travelMetadata?: {
    estimatedDuration: number;
    originType: "home" | "previous_event";
    isImpossible: boolean;
    travelBackHomeAdvised: boolean;
  };
  reminders?: { useDefault: boolean; overrides: Array<{ method: string; minutes: number; }> };
  isAllDay?: boolean; // Added for all-day toggle
  category?: string; // Added for event category
};

export const Calendar: React.FC<CalendarProps> = ({ state, dispatch }) => {
  const { t, i18n } = useTranslation();
  const userTz = state.currentUser?.timezone || 'UTC';
  const currentUserId = state.currentUser?.id || '';
  const hId = state.currentHouseholdId;
  const engine = useMemo(() => new CalendarEngine(15), []);

  // --- 🛰️ HYBRID SCOPE: Driven by global header toggle ---
  const activeScope: DataScope = state.activeDataScope ?? (hId ? 'BOTH' : 'PERSONAL');
  const [view, setView] = useState<'day' | 'week' | 'month'>('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [activeCalendarIds, setActiveCalendarIds] = useState<string[]>(['personal', 'household']);
  const [activeMembers, setActiveMembers] = useState<string[]>([currentUserId]);

  // Intelligence Layer Toggles
  const [isTravelEnabled, setTravelEnabled] = useState(false);
  const [isGhostLayerEnabled, setGhostLayerEnabled] = useState(false);

  // Modal/Dialog Control
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [isCustomCreatorOpen, setIsCustomCreatorOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<ExtendedCalendarEventPayload | null>(null);

  const [recurrenceDialog, setRecurrenceDialog] = useState<{
    isOpen: boolean;
    mode: 'edit' | 'delete';
    event: EngineEvent | null
  }>({
    isOpen: false,
    mode: 'edit',
    event: null
  });

  const currentHousehold = state.households?.find(h => h.id === hId);

  // 🤖 AI: Find Best Time
  const { callAction: callAi } = useAiAction(hId);
  const [bestTimeResult, setBestTimeResult] = useState<{ suggestions: Array<{ start: string; end: string; reason: string }> } | null>(null);
  const [bestTimeLoading, setBestTimeLoading] = useState(false);
  const [showAiPanel, setShowAiPanel] = useState(false);

  const handleFindBestTime = async () => {
    if (bestTimeLoading) return;
    setBestTimeLoading(true);
    setBestTimeResult(null);
    const today = new Date().toISOString().slice(0, 10);
    const existingEventsToday = events.filter(e => e.start?.startsWith(today)).map(e => ({ start: e.start, end: e.end, title: e.title }));
    try {
      const result = await callAi<{ suggestions: Array<{ start: string; end: string; reason: string }> }>(
        'findBestTime',
        { existingEvents: existingEventsToday, duration: 60, date: today }
      );
      if (result) setBestTimeResult(result);
    } finally {
      setBestTimeLoading(false);
    }
  };

  // 🎯 DERIVED MEMBERS FOR DISPLAY (Similar to HouseholdSettings.tsx)
  const householdMembersForDisplay = useMemo(() => {
    if (!currentHousehold || !state.userProfiles) return [];

    return currentHousehold.memberIds.map(memberId => {
      const userProfile = state.userProfiles.find(p => p.id === memberId);
      const memberRole = currentHousehold.permissions?.[memberId]?.role || Role.MEMBER;
      const memberJoinedAt = currentHousehold.permissions?.[memberId]?.joinedAt || new Date().toISOString();

      return {
        userId: memberId,
        name: userProfile?.name || t('calendar.unknown_user', 'Unknown User'),
        email: userProfile?.email || t('calendar.not_available', 'N/A'),
        role: memberRole,
        joinedAt: memberJoinedAt,
      } as HouseholdMember;
    });
  }, [currentHousehold, state.userProfiles]);

  // --- 🧠 CONTEXTUAL DATA MERGE ---
  // useCalendarState updated to filter events based on activeScope
  const { events, hasConflicts } = useCalendarState(
    state.personalEvents ?? [],
    state.householdEvents ?? [],
    state.customEvents ?? [],
    activeCalendarIds,
    currentUserId,
    hId,
    activeMembers,
    activeScope
  );

  // Window of real dates the grid currently renders — recurrence expansion only needs to
  // generate occurrences inside (a small pad around) this range.
  const visibleRange = useMemo(() => {
    if (view === 'day') return { start: startOfDay(currentDate), end: endOfDay(currentDate) };
    if (view === 'week') return { start: startOfWeek(currentDate), end: endOfWeek(currentDate) };
    // month grid shows some leading/trailing days from adjacent months
    return { start: subDays(startOfMonth(currentDate), 7), end: addDays(endOfMonth(currentDate), 7) };
  }, [view, currentDate]);

  const formattedHouseholdAddress = useMemo(() => {
    const householdAddress = currentHousehold?.address;
    return householdAddress && householdAddress.street
      ? `${householdAddress.street}, ${householdAddress.city}, ${householdAddress.state} ${householdAddress.zip}`.trim()
      : undefined;
  }, [currentHousehold]);

  // Consecutive-event address pairs needing a travel-time lookup, derived independently of
  // engine.processInstances (which needs travelDurations as an INPUT) to avoid a circular
  // dependency — expand+sort here is cheap and deterministic, so duplicating it is fine.
  const travelPairs = useMemo((): TravelPair[] => {
    if (!isTravelEnabled) return [];
    const filteredEvents: EngineEvent[] = events.filter(isEngineEvent);
    const expanded = expandRecurringEvents(filteredEvents, visibleRange.start, visibleRange.end);
    const sorted = [...expanded].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

    const pairs: TravelPair[] = [];
    let prevAddress: string | undefined = formattedHouseholdAddress;
    sorted.forEach((event) => {
      const destination = (event as any).location?.address as string | undefined;
      if (destination && prevAddress) {
        pairs.push({ key: `${prevAddress}|${destination}`, origin: prevAddress, destination });
      }
      if (destination) prevAddress = destination;
    });
    return pairs;
  }, [events, isTravelEnabled, visibleRange, formattedHouseholdAddress]);

  const travelDurations = useTravelDurations(travelPairs, (import.meta as any).env?.VITE_GOOGLE_MAPS_API_KEY);

  const processedEvents = useMemo(() => {
    const filteredEvents: EngineEvent[] = events.filter(isEngineEvent);

    return engine.processInstances(
      filteredEvents,
      userTz,
      currentUserId,
      formattedHouseholdAddress ?? null,
      isTravelEnabled,
      visibleRange,
      travelDurations
    );
  }, [events, userTz, isTravelEnabled, currentUserId, formattedHouseholdAddress, engine, visibleRange, travelDurations]);

  // --- 🛠️ PERSISTENCE HANDLERS ---
  // Any event still attached to a live series (a virtual occurrence instance, flagged by
  // recurrence.ts) must go through the scope dialog before writing. A detached single-occurrence
  // override (no `isRecurrenceInstance` flag) is just a normal event at this point and skips it.
  const handleSaveEvent = async (eventData: ExtendedCalendarEventPayload) => {
    if (eventData.isRecurrenceInstance && eventData.seriesId && eventData.originalStartTime?.dateTime) {
      setRecurrenceDialog({ isOpen: true, mode: 'edit', event: eventData as EngineEvent });
      return;
    }
    await persistEventSave(eventData);
  };

  const persistEventSave = async (eventData: ExtendedCalendarEventPayload) => { // 🎯 eventData can be partial for new events
    const batch = writeBatch(db);

    // 🛡️ DATA PATH RESOLUTION
    // If no hId exists, force 'personal'. Otherwise, check eventData.scope
    const determinedScope: 'personal' | 'household' = (!hId || eventData.scope === 'personal') ? 'personal' : 'household';
    const isPersonalScope = !hId || eventData.scope === 'personal';

    // 🔒 PERMISSION CHECK for Household writes
    if (!isPersonalScope) {
      const permissions = currentHousehold?.permissions?.[currentUserId];
      if (!permissions?.canManageEvents) {
        showToast(t('calendar.permission_denied_household_write', "Permission Denied: You cannot write to the Household agenda."), 'error');
        return;
      }
    }

    const collectionPath = isPersonalScope
      ? `users/${currentUserId}/events`
      : `households/${hId}/events`;

    const eventRef = eventData.id
      ? doc(db, collectionPath, eventData.id)
      : doc(collection(db, collectionPath));

    type FinalEventDataType = Omit<EngineEvent, 'travelMetadata'> & ExtendedCalendarEventPayload;

    const baseEventData: FinalEventDataType = {
      id: eventRef.id,
      title: eventData.title || t('calendar.untitled_event', 'Untitled Event'),
      start: eventData.start || new Date().toISOString(),
      end: eventData.end || new Date(Date.now() + 60 * 60 * 1000).toISOString(),
      color: eventData.color || '#5e88fc',
      source: determinedScope,
      scope: determinedScope,
      ownerId: isPersonalScope ? currentUserId : (hId || currentUserId || ''),
      ...eventData,
      // Always reflects the true current actor regardless of what the payload carries — household
      // events have no other per-actor field (ownerId holds the household id, not a user id) and
      // without this, usage-analytics capture has no way to attribute a household-scope interaction.
      lastModifiedBy: currentUserId,
    };

    // Filter out undefined values
    const finalEventData = Object.fromEntries(
      Object.entries(baseEventData).filter(([, value]) => value !== undefined)
    ) as FinalEventDataType;

    batch.set(eventRef, finalEventData, { merge: true });

  try {
      await batch.commit();
      setIsEventModalOpen(false);
      setSelectedEvent(null);

      // 🎯 OPTIMISTIC UI UPDATE: Dispatch action to update local state immediately
      if (isPersonalScope) {
        if (eventData.id) { // Existing event
          dispatch({ type: 'UPDATE_PERSONAL_EVENT', payload: { id: finalEventData.id, updates: finalEventData } });
        } else { // New event
          dispatch({ type: 'ADD_PERSONAL_EVENT', payload: finalEventData });
        }
      } else { // Household scope
        if (eventData.id) { // Existing event
          dispatch({ type: 'UPDATE_HOUSEHOLD_EVENT', payload: { id: finalEventData.id, updates: finalEventData } });
        } else { // New event
          dispatch({ type: 'ADD_HOUSEHOLD_EVENT', payload: finalEventData });
        }
      }
    } catch (error) {
      console.error("Error saving event to Firestore and updating state:", error);
      showToast(t('calendar.save_error', 'Failed to save event. Please try again.'), 'error');
    }
  };

  const handleDeleteEvent = async (eventId: string, eventScope: 'personal' | 'household') => {
    if (!eventId) return;
    if (selectedEvent?.isRecurrenceInstance && selectedEvent.seriesId && selectedEvent.originalStartTime?.dateTime) {
      setRecurrenceDialog({ isOpen: true, mode: 'delete', event: selectedEvent as EngineEvent });
      return;
    }
    await persistEventDelete(eventId, eventScope);
  };

  const persistEventDelete = async (eventId: string, eventScope: 'personal' | 'household') => {
    const batch = writeBatch(db);

    const collectionPath = eventScope === 'personal'
      ? `users/${currentUserId}/events`
      : `households/${hId}/events`;

    const eventRef = doc(db, collectionPath, eventId);
    batch.delete(eventRef);

    try {
      await batch.commit();
      setIsEventModalOpen(false);
      setSelectedEvent(null);

      // Optimistic UI Update: Dispatch action to update local state immediately
      if (eventScope === 'personal') {
        dispatch({ type: 'DELETE_PERSONAL_EVENT', payload: eventId });
      } else {
        dispatch({ type: 'DELETE_HOUSEHOLD_EVENT', payload: eventId });
      }
    } catch (error) {
      console.error("Error deleting event from Firestore and updating state:", error);
      showToast(t('calendar.delete_error', 'Failed to delete event. Please try again.'), 'error');
    }
  }

  // --- 🔁 RECURRENCE SCOPE RESOLUTION ---
  // Fields that only make sense on a virtual/synthetic instance and must never be written
  // back onto a real Firestore doc (master or override).
  const stripSyntheticFields = (data: ExtendedCalendarEventPayload) => {
    const { id, seriesId, isRecurrenceInstance, originalStartTime, ...rest } = data as any;
    return rest;
  };

  const handleRecurrenceChoice = async (choice: RecurrenceChoice) => {
    const target = recurrenceDialog.event as ExtendedCalendarEventPayload | null;
    setRecurrenceDialog(prev => ({ ...prev, isOpen: false }));
    if (!target || !target.seriesId || !target.originalStartTime?.dateTime) return;

    const masterId = target.seriesId;
    const originalStart = new Date(target.originalStartTime.dateTime);
    const writeScope: 'personal' | 'household' = (target.scope as any) || (hId ? 'household' : 'personal');
    const collectionPath = writeScope === 'personal' ? `users/${currentUserId}/events` : `households/${hId}/events`;
    const masterRef = doc(db, collectionPath, masterId);

    if (writeScope === 'household' && !hId) return;
    if (writeScope === 'household' && !currentHousehold?.permissions?.[currentUserId]?.canManageEvents) {
      showToast(t('calendar.permission_denied_household_write', "Permission Denied: You cannot write to the Household agenda."), 'error');
      return;
    }

    try {
      const masterSnap = await getDoc(masterRef);
      if (!masterSnap.exists()) {
        showToast(t('calendar.recurrence_master_missing', 'This recurring series no longer exists.'), 'error');
        return;
      }
      const masterData = masterSnap.data() as ExtendedCalendarEventPayload;
      const masterRecurrence = masterData.recurrence || [];

      if (recurrenceDialog.mode === 'edit') {
        const edited = stripSyntheticFields(target);

        if (choice === 'instance') {
          const overrideRef = doc(collection(db, collectionPath));
          await setDoc(overrideRef, {
            ...edited,
            id: overrideRef.id,
            recurrence: [],
            recurringEventId: masterId,
            originalStartTime: target.originalStartTime,
            lastModifiedBy: currentUserId,
          });
        } else if (choice === 'future') {
          const batch = writeBatch(db);
          batch.update(masterRef, {
            recurrence: clampRecurrenceUntil(masterRecurrence, originalStart),
            lastModifiedBy: currentUserId,
          });
          const newSeriesRef = doc(collection(db, collectionPath));
          batch.set(newSeriesRef, {
            ...edited,
            id: newSeriesRef.id,
            recurrence: stripRecurrenceBounds(masterRecurrence),
            lastModifiedBy: currentUserId,
          });
          await batch.commit();
        } else {
          // full series — update the master in place; RRULE frequency/byday is unchanged,
          // but the edited start's clock-time now governs every future occurrence.
          await setDoc(masterRef, { ...edited, id: masterId, recurrence: masterRecurrence, lastModifiedBy: currentUserId }, { merge: true });
        }
      } else {
        // delete
        if (choice === 'instance') {
          await setDoc(masterRef, { recurrence: addExdateToRecurrence(masterRecurrence, originalStart) }, { merge: true });
        } else if (choice === 'future') {
          await setDoc(masterRef, { recurrence: clampRecurrenceUntil(masterRecurrence, originalStart) }, { merge: true });
        } else {
          const batch = writeBatch(db);
          batch.delete(masterRef);
          const overridesSnap = await getDocs(query(collection(db, collectionPath), where('recurringEventId', '==', masterId)));
          overridesSnap.forEach(d => batch.delete(d.ref));
          await batch.commit();
        }
      }

      setIsEventModalOpen(false);
      setSelectedEvent(null);
      showToast(t('calendar.recurrence_updated', 'Recurring event updated.'), 'success');
    } catch (error) {
      console.error('Error resolving recurrence choice:', error);
      showToast(t('calendar.recurrence_error', 'Failed to update the recurring event. Please try again.'), 'error');
    }
  };

  // --- 🕹️ NAVIGATION ---
  const handleNavigate = (dir: 'back' | 'forward' | 'today') => {
    if (dir === 'today') {
      setCurrentDate(new Date());
      return;
    }
    const step = dir === 'forward' ? 1 : -1;
    if (view === 'day') setCurrentDate(prev => step > 0 ? addDays(prev, 1) : subDays(prev, 1));
    if (view === 'week') setCurrentDate(prev => step > 0 ? addWeeks(prev, 1) : subWeeks(prev, 1));
    if (view === 'month') setCurrentDate(prev => step > 0 ? addMonths(prev, 1) : subMonths(prev, 1));
  };

    const handleEventClick = (event: EngineEvent) => {
    setSelectedEvent(event);
    setIsEventModalOpen(true);
    // console.log("handleAddNewEvent: isEventModalOpen set to true.", { selectedEvent, isEventModalOpen: true });
  };

  const handleAddNewEvent = (initialStart?: Date, initialEnd?: Date) => {
    setSelectedEvent({
      start: initialStart?.toISOString() || new Date().toISOString(),
      end: initialEnd?.toISOString() || new Date(Date.now() + 60 * 60 * 1000).toISOString(),
      isAllDay: false, // Default for new events
      category: 'General', // Default for new events
      // Initialize other fields to avoid type issues
      googleEventId: undefined,
      etag: undefined,
      status: 'confirmed',
      htmlLink: undefined,
      iCalUID: undefined,
      recurrence: [],
      recurringEventId: undefined,
      originalStartTime: undefined,
      transparency: 'opaque',
      visibility: 'default',
      organizer: undefined,
      attendees: [],
      hangoutLink: undefined,
      creatorId: undefined,
      templateType: undefined,
      travelMetadata: undefined,
      reminders: undefined,
    });
    setIsEventModalOpen(true);
  };

  return (
    <div className="flex flex-col h-screen w-full bg-white dark:bg-slate-950 overflow-hidden calendar-view-lock">

      <CalendarToolbar
        view={view}
        onViewChange={setView}
        onToday={() => handleNavigate('today')}
        onPrev={() => handleNavigate('back')}
        onNext={() => handleNavigate('forward')}
        currentDateLabel={format(currentDate, 'MMMM yyyy')}
        isTravelEnabled={isTravelEnabled}
        setTravelEnabled={setTravelEnabled}
        isGhostLayerEnabled={isGhostLayerEnabled}
        setGhostLayerEnabled={setGhostLayerEnabled}
        activeMembers={activeMembers}
        onMemberToggle={(id) => setActiveMembers(prev => prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id])}
        householdMembers={householdMembersForDisplay}
        customCalendars={(state as any).customCalendars || []}
        activeCalendarIds={activeCalendarIds}
        onCalendarToggle={(id) => setActiveCalendarIds(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id])}
        onAddNewCalendar={() => setIsCustomCreatorOpen(true)}
        hasConflicts={hasConflicts}
        activeScope={activeScope}
        onScopeChange={(scope: DataScope) => dispatch({ type: 'SET_DATA_SCOPE', payload: scope })}
        currentHouseholdId={hId}
        onAddNewEvent={handleAddNewEvent}
        onAiFindBestTime={() => { setShowAiPanel(true); if (!bestTimeResult && !bestTimeLoading) handleFindBestTime(); }}
        className="relative z-40"
      />

      {/* AI Best Time Panel — collapsed by default, expands on demand */}
      {showAiPanel && (
        <section aria-labelledby="ai-panel-heading" className="px-4 py-2 flex items-center gap-3 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800 animate-in slide-in-from-top-2 duration-200">
          <h2 id="ai-panel-heading" className="sr-only">AI Suggestions</h2>
          <button
            onClick={() => setShowAiPanel(false)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-brand/10 text-brand text-[9px] font-black uppercase tracking-widest rounded-xl hover:bg-brand/20 transition-colors border border-brand/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            <Sparkles className="w-3 h-3" /> AI Best Time
          </button>
          {bestTimeLoading && <Loader2 className="w-3 h-3 animate-spin text-brand" />}
          {bestTimeResult?.suggestions && (
            <ul id="ai-suggestions-list" role="list" aria-live="polite" className="flex items-center gap-2 overflow-x-auto">
              {bestTimeResult.suggestions.slice(0, 3).map((s, i) => (
                <li key={i}>
                  <button className="flex-shrink-0 px-3 py-1.5 bg-brand/5 rounded-xl border border-brand/20 hover:bg-brand/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand transition-colors">
                    <p className="text-2xs font-black text-brand uppercase whitespace-nowrap tracking-wider">
                      {new Date(s.start).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })} — {new Date(s.end).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}
                    </p>
                    <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5 whitespace-nowrap">{s.reason}</p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <div className="flex-1 relative overflow-hidden z-10">
        <CalendarGrid
          view={view}
          events={processedEvents}
          activeMembers={activeMembers}
          isTravelEnabled={isTravelEnabled}
          isGhostLayerEnabled={isGhostLayerEnabled}
          hourHeight={80}
          householdMembers={householdMembersForDisplay}
          currentUserId={currentUserId}
          currentHouseholdId={hId}
          onEventClick={handleEventClick}
          activeScope={activeScope}
          currentDate={currentDate}
          onViewChange={setView}
          onDateChange={setCurrentDate}
          onDragToCreate={handleAddNewEvent}
        />
      </div>

      <EventModal
        isOpen={isEventModalOpen}
        onClose={() => {
          setIsEventModalOpen(false);
          setSelectedEvent(null); // Clear selected event when closing
        }}
        onSave={handleSaveEvent}
        onDelete={handleDeleteEvent}
        initialData={selectedEvent || {
          title: '',
          description: '',
          source: 'personal',
          start: new Date().toISOString(),
          end: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
          color: '#5e88fc',
          status: 'confirmed',
          transparency: 'opaque',
          visibility: 'default',
          recurrence: [],
          attendees: [],
          googleEventId: undefined,
          etag: undefined,
          htmlLink: undefined,
          iCalUID: undefined,
          recurringEventId: undefined,
          originalStartTime: undefined,
          organizer: undefined,
          hangoutLink: undefined,
          creatorId: undefined,
          templateType: undefined,
          travelMetadata: undefined,
          reminders: undefined,
        }}
        userTimezone={userTz}
        state={state}
        contacts={currentHousehold?.contacts || []}
        defaultScope={activeScope === 'SHARED' ? 'household' : 'personal'}
        householdMembers={householdMembersForDisplay}
      />

      <CustomCalendarCreator
        isOpen={isCustomCreatorOpen}
        onClose={() => setIsCustomCreatorOpen(false)}
        currentUserId={currentUserId}
        currentHouseholdId={hId}
        onSave={async (newCal: Partial<CustomCalendar>, targetScope: 'personal' | 'household') => {
          try {
            const path = targetScope === 'household' && hId
              ? collection(db, 'households', hId, 'calendars')
              : collection(db, 'users', currentUserId, 'calendars');
            const ref = doc(path);
            await setDoc(ref, { ...newCal, id: ref.id, createdAt: serverTimestamp() });
            showToast(t('calendar.calendar_created', 'Calendar created!'), 'success');
          } catch {
            showToast(t('calendar.calendar_create_error', 'Failed to create calendar. Please try again.'), 'error');
          }
        }}
      />

      <RecurrenceActionDialog
        isOpen={recurrenceDialog.isOpen}
        mode={recurrenceDialog.mode}
        onClose={() => setRecurrenceDialog(prev => ({ ...prev, isOpen: false }))}
        onSelect={handleRecurrenceChoice}
        eventScope={recurrenceDialog.event?.scope || 'personal'}
        eventTitle={recurrenceDialog.event?.title}
        userRole={currentHousehold?.permissions?.[currentUserId]?.role}
      />
    </div>
  );
};